import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  linkInline,
  linkInlineMuted,
  FERTILITY_DEMO_SECTION_BORDER,
  introCardKeyline,
  introHeadlineSerifDeep,
  DEMO_SECTION_STACK_GAP_PX,
  DEMO_SECTION_CARD_STACK_GAP_PX,
  DEMO_MINI_CARD_GRID_GAP_PX,
  demoSectionCardStackStyle,
} from '../designSystem';
import { NovaUnifiedInlineAnalysis, NovaUnifiedMediumHeader } from '../components/NovaUnified';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";
const inputStyle = { padding: 6, borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, width: '100%' };

/** Same treatment as `FertilityDemoHome` `CardSectionHeader` trailing (`See medications →`, `See all →`). */
const healthSectionHeaderLink = {
  ...linkInline,
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--pink, #d4537e)',
  textTransform: 'none',
  letterSpacing: 0,
  whiteSpace: 'nowrap',
};
const healthSectionHeaderDone = {
  ...linkInline,
  fontSize: 12,
  fontWeight: 600,
  color: '#0F6E56',
  textTransform: 'none',
  letterSpacing: 0,
  whiteSpace: 'nowrap',
};
const healthSectionHeaderMutedLink = {
  ...linkInlineMuted,
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'none',
  letterSpacing: 0,
  whiteSpace: 'nowrap',
};

const btnSecondary = { padding: '6px 12px', background: 'var(--sand)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 999, cursor: 'pointer', fontSize: 12 };

const LS_KEY = 'orivonai:fertilityDemoHealthV1';

const INITIAL_STATS = [
  { label: 'AMH', val: '2.1', unit: 'ng/mL', range: 'Normal 1.0–4.0', badge: 'In range', badgeGreen: true },
  { label: 'FSH', val: '7.2', unit: 'mIU/mL', range: 'Normal 3–10', badge: 'In range', badgeGreen: true },
  { label: 'AFC', val: '12', unit: null, range: 'Normal 6–12', badge: 'High normal', badgeGreen: false },
  { label: 'Estradiol', val: '45', unit: 'pg/mL', range: 'Normal 30–100', badge: 'In range', badgeGreen: true },
];

/** Ovarian reserve / initial workup panel — default for demo draws (8 markers). */
const OVARIAN_RESERVE_PANEL_LABS = [
  { id: '1', name: 'AMH', range: 'Normal 1.0–4.0', val: '2.4 ng/mL', date: 'Day 3 panel', borderline: false },
  { id: '2', name: 'FSH', range: 'Normal 3–10 mIU/mL', val: '6.1 mIU/mL', date: 'Day 3 panel', borderline: false },
  { id: '3', name: 'AFC', range: 'Normal 6–12', val: '11', date: 'Day 3 panel', borderline: false },
  { id: '4', name: 'Estradiol', range: 'Normal 30–100 pg/mL', val: '38 pg/mL', date: 'Day 3 panel', borderline: false },
  { id: '5', name: 'LH', range: 'Normal 2–15 mIU/mL', val: '4.2 mIU/mL', date: 'Day 3 panel', borderline: false },
  { id: '6', name: 'TSH', range: 'Normal 0.4–4.0 mIU/L', val: '1.8 mIU/L', date: 'Day 3 panel', borderline: false },
  { id: '7', name: 'Prolactin', range: 'Normal 2–29 ng/mL', val: '11.3 ng/mL', date: 'Day 3 panel', borderline: false },
  { id: '8', name: 'Progesterone', range: 'Normal >10 ng/mL (mid-luteal)', val: '14.2 ng/mL', date: 'Day 21 draw', borderline: false },
];

/** @deprecated use OVARIAN_RESERVE_PANEL_LABS — kept name for addBloodDraw template */
const FOLLOWUP_LABS = OVARIAN_RESERVE_PANEL_LABS;

const INITIAL_CLINIC = {
  name: 'Ohio Fertility Network',
  location: 'Columbus, OH',
  doctor: 'Dr. Kim',
  protocol: 'Antagonist',
  cycle: 'IVF · Cycle 2',
  nextAppt: 'Tomorrow · Monitoring scan',
  phone: '(614) 555-0182',
};

const INITIAL_CYCLE_ROWS = [
  { key: 'Current cycle', val: 'Cycle 2 · Stim day 8', pill: 'pink' },
  { key: 'Cycle 1 outcome', val: '4 eggs retrieved', pill: 'green' },
  { key: 'Protocol', val: 'Antagonist', pill: null },
  { key: 'Medications', val: 'Gonal-F · Menopur · Cetrotide', pill: null },
  { key: 'Est. retrieval', val: '~Mar 25 · 6 days away', pill: null },
  { key: 'Follicles responding', val: '6 follicles', pill: null },
];

function drawId() {
  return `draw_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function formatDrawTabLabel(draw) {
  if (draw.tabLabel) return draw.tabLabel;
  if (draw.id === 'baseline' || draw.isBaseline) return 'Baseline';
  if (draw.dateISO) {
    const d = new Date(draw.dateISO + 'T12:00:00');
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return 'Draw';
}

/** Split values like "2.4 ng/mL" for 16px primary number + 11px unit (type scale). */
function splitLabDisplayVal(val) {
  const s = String(val ?? '').trim();
  const m = s.match(/^([\d.,]+)\s*(.*)$/);
  if (!m || !m[1]) return { num: s, unit: '', plain: true };
  return { num: m[1], unit: (m[2] || '').trim(), plain: false };
}

const EDU_FOOTER = 'Educational context — your care team has the full picture.';

/** Deterministic educational copy for Key numbers cards (demo — no API). */
function getKeyNumberAnalysis(s) {
  const label = String(s.label || '').trim();
  const val = String(s.val ?? '').trim();
  const unit = s.unit ? String(s.unit) : '';
  const range = String(s.range || '');
  const badge = String(s.badge || '');
  const ok = s.badgeGreen !== false && /in range/i.test(badge);

  let sentence = '';
  let question = '';

  if (label === 'AMH') {
    sentence = ok
      ? `AMH reflects ovarian reserve. ${val}${unit ? ` ${unit}` : ''} falls within the reference range shown — a pattern many clinics use alongside age and ultrasound.`
      : `AMH reflects ovarian reserve. ${val}${unit ? ` ${unit}` : ''} is labeled "${badge}" here — your REI interprets this with your age, AFC, and plan.`;
    question = 'How does my AMH fit with the protocol and timeline we discussed?';
  } else if (label === 'FSH') {
    sentence = ok
      ? `FSH helps describe how hard your ovaries work to recruit follicles. ${val}${unit ? ` ${unit}` : ''} sits in the typical lab window on your card.`
      : `FSH helps describe ovarian response potential. This value is flagged "${badge}" — ask what that means for your cycle.`;
    question = 'Should we repeat FSH on a specific cycle day, or is this draw enough for planning?';
  } else if (label === 'AFC' || label === 'AFC (antral)') {
    sentence = ok
      ? `Antral follicle count estimates how many small follicles could respond to stimulation. ${val} is in range on your record — clinics pair this with AMH and age.`
      : `Your AFC is marked "${badge}" — it is one piece of the picture; your team weighs it with hormones and scan timing.`;
    question = 'How does my AFC line up with the stimulation dose we are using?';
  } else if (/estradiol|e2/i.test(label)) {
    sentence = ok
      ? `Estradiol reflects follicle activity and rises during stims. ${val}${unit ? ` ${unit}` : ''} matches the reference band shown — timing in the cycle matters as much as the number.`
      : `Estradiol is interpreted in context of scan and protocol. This result is labeled "${badge}" — your clinic ties it to follicle sizes.`;
    question = 'What E2 trajectory are you aiming for before trigger in my protocol?';
  } else {
    sentence = `${label} is tracked on your health record. ${val}${unit ? ` ${unit}` : ''} — compare with ${range || 'your lab’s reference'} when you review with your team.`;
    question = `What should I know about ${label} in my specific protocol?`;
  }

  return { sentence, band: badge || (ok ? 'In range' : 'Review with clinic'), question };
}

/** Inline analysis for a lab row (demo — mirrors Nova educational style). */
function getLabRowAnalysis(l) {
  const name = String(l.name || 'Lab').trim();
  const val = String(l.val || '').trim();
  const range = String(l.range || '');
  const borderline = Boolean(l.borderline);
  const lower = name.toLowerCase();

  let sentence = '';
  let question = '';
  /** Band line (Nova) — shown only for LH/TSH/Prolactin/Progesterone per panel spec */
  let band;

  if (/^amh$/i.test(lower)) {
    sentence = borderline
      ? `AMH reflects egg reserve over months to years. ${val} — your team may read this alongside age and AFC.`
      : `AMH reflects egg reserve. ${val} sits in the reference band on this draw when viewed with ${range || 'standard ranges'}.`;
    question = 'How does this AMH compare with what you expected for my age?';
  } else if (/fsh/i.test(lower)) {
    sentence = `FSH helps describe ovarian responsiveness. ${val} is one snapshot — cycle day and clinic cutoffs matter.`;
    question = 'Was this FSH drawn on the right cycle day for how you use it?';
  } else if (/afc/i.test(lower)) {
    sentence = `AFC estimates follicles available to recruit. ${val} pairs with AMH and your response in prior cycles.`;
    question = 'Does this AFC change anything about our stim plan?';
  } else if (/estradiol|e2/i.test(lower)) {
    sentence = borderline
      ? `Estradiol rises as follicles grow. ${val} — your clinic will interpret it with ultrasound and protocol phase.`
      : `Estradiol reflects follicle estrogen output. ${val} is shown against ${range || 'your lab range'}.`;
    question = 'What E2 range are you targeting before trigger for me?';
  } else if (/^lh$/i.test(lower)) {
    sentence =
      "LH on day 3 helps confirm your cycle isn't starting with an early surge. 4.2 mIU/mL is within the expected day 3 window.";
    question = 'Should my LH be rechecked at any point in my protocol?';
    band = 'In range';
  } else if (/^tsh$/i.test(lower)) {
    sentence =
      'Thyroid function affects cycle regularity and embryo development. 1.8 mIU/L is well within the range most fertility clinics use.';
    question = 'Do you want my TSH rechecked before transfer?';
    band = 'In range';
  } else if (/prolactin/i.test(lower)) {
    sentence =
      'Elevated prolactin can suppress ovulation. 11.3 ng/mL is in the normal range — your clinic checked this to rule it out.';
    question = 'Would stress or timing affect this result if retested?';
    band = 'In range';
  } else if (/progesterone/i.test(lower)) {
    sentence =
      'A day 21 progesterone above 10 ng/mL suggests ovulation occurred that cycle. 14.2 ng/mL is a clear confirmation.';
    question = 'Is this level strong enough, or would you want it higher?';
    band = 'In range';
  } else {
    sentence = `${name} is logged for this draw. ${val}${range ? ` — reference: ${range}` : ''}.`;
    question = `What does ${name} tell you about my cycle at this point?`;
  }

  return { sentence, question, borderline, band };
}

function InlineNovaAnalysis({ sentence, band, question, compact, showBand = true }) {
  return (
    <NovaUnifiedInlineAnalysis compact={compact}>
      <NovaUnifiedMediumHeader contextPill="Analysis" />
      <p style={{ fontSize: 12, color: '#4b1528', lineHeight: 1.5, margin: '0 0 6px 0' }}>{sentence}</p>
      {showBand && band ? (
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>Band: {band}</div>
      ) : null}
      <div style={{ fontSize: 10, color: 'var(--pink-d, #993556)', lineHeight: 1.4, fontStyle: 'italic', marginBottom: 6 }}>Ask your doctor: {question}</div>
      <div style={{ fontSize: 9, color: 'var(--muted)', lineHeight: 1.35 }}>{EDU_FOOTER}</div>
    </NovaUnifiedInlineAnalysis>
  );
}

function CollapsibleInlineNovaAnalysis({ defaultOpen = true, ...analysisProps }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 6 }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--pink-d, #993556)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 0 6px',
          fontFamily: FONT_BODY,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span>Nova analysis</span>
        <span aria-hidden style={{ fontSize: 10, color: 'var(--muted)' }}>
          {open ? '▲' : '▾'}
        </span>
      </button>
      {open ? <InlineNovaAnalysis {...analysisProps} /> : null}
    </div>
  );
}

function SectionCard({
  sectionTitle,
  sectionDescriptor,
  children,
  editing,
  onEditToggle,
  onExport,
  onAddResult,
  descriptorMultiline,
  style: sectionStyle,
}) {
  const descStyle =
    descriptorMultiline === true
      ? { margin: '3px 0 0 0', whiteSpace: 'pre-line' }
      : { margin: '3px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div style={{ background: 'var(--white)', border: FERTILITY_DEMO_SECTION_BORDER, borderRadius: 14, padding: '16px 18px', ...sectionStyle }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="section-title">{sectionTitle}</h2>
          {sectionDescriptor ? (
            <p className="section-descriptor" style={descStyle}>
              {sectionDescriptor}
            </p>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flexShrink: 0, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button type="button" onClick={onEditToggle} style={editing ? healthSectionHeaderDone : healthSectionHeaderLink}>
            {editing ? 'Done' : 'Edit →'}
          </button>
          {typeof onAddResult === 'function' ? (
            <button type="button" onClick={onAddResult} style={healthSectionHeaderLink}>
              Add result →
            </button>
          ) : null}
          {onExport ? (
            <button type="button" onClick={onExport} style={healthSectionHeaderMutedLink}>
              Share with doctor →
            </button>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}

function buildDefaultPersist() {
  return {
    version: 1,
    activeDrawId: 'baseline',
    bloodDraws: [{ id: 'baseline', dateISO: '2025-01-15', isBaseline: true, tabLabel: 'Day 3 panel · Jan 15, 2025' }],
    labsByDraw: {
      baseline: OVARIAN_RESERVE_PANEL_LABS.map((l) => ({ ...l })),
    },
    stats: INITIAL_STATS.map((s) => ({ ...s })),
    clinic: { ...INITIAL_CLINIC },
    cycleRows: INITIAL_CYCLE_ROWS.map((r) => ({ ...r })),
  };
}

export default function FertilityDemoHealth({ navigate, omitHeader, demoChapter = 'fertility' }) {
  const { user, getHealthRecord, updateHealthRecord } = useApp();
  const defaultPersist = useMemo(() => buildDefaultPersist(), []);

  const [stats, setStats] = useState(defaultPersist.stats);
  const [clinic, setClinic] = useState(defaultPersist.clinic);
  const [cycleRows, setCycleRows] = useState(defaultPersist.cycleRows);
  const [bloodDraws, setBloodDraws] = useState(defaultPersist.bloodDraws);
  const [labsByDraw, setLabsByDraw] = useState(defaultPersist.labsByDraw);
  const [activeDrawId, setActiveDrawId] = useState(defaultPersist.activeDrawId);

  const [editingStats, setEditingStats] = useState(false);
  const [editingLabs, setEditingLabs] = useState(false);
  const [editingClinic, setEditingClinic] = useState(false);
  const [editingCycle, setEditingCycle] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const persistReady = useRef(false);

  const labs = labsByDraw[activeDrawId] || [];

  const persistFull = useCallback(
    (payload) => {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(payload));
      } catch {
        /* ignore */
      }
      const email = user?.email && String(user.email).toLowerCase().trim();
      if (email) updateHealthRecord({ fertilityDemoHealthV1: payload });
    },
    [user?.email, updateHealthRecord]
  );

  useEffect(() => {
    let merged = null;
    try {
      const rawLs = localStorage.getItem(LS_KEY);
      if (rawLs) merged = JSON.parse(rawLs);
    } catch {
      merged = null;
    }
    const hr = getHealthRecord?.();
    const embedded = hr?.fertilityDemoHealthV1;
    const base = embedded && typeof embedded === 'object' ? embedded : merged;
    if (!base || typeof base !== 'object') {
      persistReady.current = true;
      return;
    }
    const def = buildDefaultPersist();
    if (Array.isArray(base.stats)) setStats(base.stats);
    if (base.clinic && typeof base.clinic === 'object') setClinic({ ...def.clinic, ...base.clinic });
    if (Array.isArray(base.cycleRows)) setCycleRows(base.cycleRows);
    if (Array.isArray(base.bloodDraws) && base.bloodDraws.length) {
      const hasBaseline = base.bloodDraws.some((d) => d.id === 'baseline' || d.isBaseline);
      setBloodDraws(hasBaseline ? base.bloodDraws : def.bloodDraws);
    }
    if (base.labsByDraw && typeof base.labsByDraw === 'object') {
      const next = { ...base.labsByDraw };
      if (!next.baseline && def.labsByDraw.baseline) next.baseline = def.labsByDraw.baseline;
      setLabsByDraw(next);
    }
    if (base.activeDrawId && typeof base.activeDrawId === 'string') setActiveDrawId(base.activeDrawId);
    persistReady.current = true;
  }, [getHealthRecord]);

  useEffect(() => {
    if (!persistReady.current) return;
    const t = setTimeout(() => {
      persistFull({
        version: 1,
        activeDrawId,
        bloodDraws,
        labsByDraw,
        stats,
        clinic,
        cycleRows,
      });
    }, 320);
    return () => clearTimeout(t);
  }, [activeDrawId, bloodDraws, labsByDraw, stats, clinic, cycleRows, persistFull]);

  const updateStat = (idx, field, value) => {
    const next = [...stats];
    next[idx] = { ...next[idx], [field]: value };
    setStats(next);
  };

  const updateLab = (idx, field, value) => {
    if (activeDrawId === 'baseline' && field !== 'borderline') {
      /* allow baseline edits but never replace draw list baseline */
    }
    const rows = [...(labsByDraw[activeDrawId] || [])];
    rows[idx] = { ...rows[idx], [field]: value };
    setLabsByDraw((prev) => ({ ...prev, [activeDrawId]: rows }));
  };

  const addLab = () => {
    const rows = [...(labsByDraw[activeDrawId] || [])];
    rows.push({
      id: String(Date.now()),
      name: 'New lab',
      range: '',
      val: '',
      date: formatDrawTabLabel(bloodDraws.find((d) => d.id === activeDrawId) || {}),
      borderline: false,
    });
    setLabsByDraw((prev) => ({ ...prev, [activeDrawId]: rows }));
  };

  const removeLab = (idx) => {
    const rows = [...(labsByDraw[activeDrawId] || [])];
    if (rows.length <= 1) return;
    rows.splice(idx, 1);
    setLabsByDraw((prev) => ({ ...prev, [activeDrawId]: rows }));
  };

  const addBloodDraw = () => {
    const id = drawId();
    const iso = new Date().toISOString().slice(0, 10);
    const tabLabel = new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const template = (labsByDraw[activeDrawId] || FOLLOWUP_LABS).map((l) => ({
      ...l,
      id: String(Math.random()).slice(2, 10),
      date: tabLabel,
    }));
    setBloodDraws((prev) => [...prev, { id, dateISO: iso, isBaseline: false, tabLabel }]);
    setLabsByDraw((prev) => ({ ...prev, [id]: template }));
    setActiveDrawId(id);
  };

  const updateCycleRow = (idx, field, value) => {
    const next = [...cycleRows];
    next[idx] = { ...next[idx], [field]: value };
    setCycleRows(next);
  };

  const addCycleRow = () => {
    setCycleRows([...cycleRows, { key: 'New field', val: '', pill: null }]);
  };

  const removeCycleRow = (idx) => {
    if (cycleRows.length > 1) setCycleRows(cycleRows.filter((_, i) => i !== idx));
  };

  const exportSection = (title, content) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `HealthRecord_${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const heroCardStyle = { background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', ...introCardKeyline, padding: '22px 26px' };
  const topLabelStyle = { fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#993556', marginBottom: 5 };
  const heroTextStyle = introHeadlineSerifDeep;
  const sublineStyle = { fontSize: 13, color: 'rgba(60,30,45,0.55)', marginBottom: 0 };

  const sortedDrawsForTabs = useMemo(() => {
    const rest = bloodDraws.filter((d) => d.id !== 'baseline' && !d.isBaseline);
    rest.sort((a, b) => String(a.dateISO || '').localeCompare(String(b.dateISO || '')));
    const base = bloodDraws.find((d) => d.id === 'baseline' || d.isBaseline);
    return base ? [base, ...rest] : bloodDraws;
  }, [bloodDraws]);

  return (
    <div style={{ fontFamily: FONT_BODY, width: '100%', boxSizing: 'border-box' }}>
      <div style={demoSectionCardStackStyle}>
      {!omitHeader && (
          <div
            style={{
              ...heroCardStyle,
              marginBottom: DEMO_SECTION_STACK_GAP_PX - DEMO_SECTION_CARD_STACK_GAP_PX,
            }}
          >
            <div style={topLabelStyle}>Health Record · Fertility · Cycle 2</div>
            <div style={heroTextStyle}>Your health at a glance</div>
            <div style={sublineStyle}>
              Labs, clinic info, and cycle history — edit any section below to add or update. Sign up to save and share with your doctor.
            </div>
          </div>
      )}

      <SectionCard
        sectionTitle="Clinic & appointments"
        sectionDescriptor="Your care team and next appointment."
        editing={editingClinic}
        onEditToggle={() => setEditingClinic(!editingClinic)}
      >
        {editingClinic ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  background: 'var(--sage-l, #E1F5EE)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                🏥
              </div>
              <div style={{ flex: 1, display: 'grid', gap: 8 }}>
                <input value={clinic.name} onChange={(e) => setClinic({ ...clinic, name: e.target.value })} style={inputStyle} placeholder="Clinic name" />
                <input value={clinic.location} onChange={(e) => setClinic({ ...clinic, location: e.target.value })} style={inputStyle} placeholder="Location" />
                <input value={clinic.doctor} onChange={(e) => setClinic({ ...clinic, doctor: e.target.value })} style={inputStyle} placeholder="Doctor" />
              </div>
            </div>
            {[
              { key: 'Protocol', val: clinic.protocol, field: 'protocol' },
              { key: 'Cycle', val: clinic.cycle, field: 'cycle' },
              { key: 'Next appt', val: clinic.nextAppt, field: 'nextAppt', urgent: true },
              { key: 'Phone', val: clinic.phone, field: 'phone' },
            ].map((r) => (
              <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--sand-2)', gap: 8 }}>
                <span style={{ color: 'var(--muted)' }}>{r.key}</span>
                <input value={r.val} onChange={(e) => setClinic({ ...clinic, [r.field]: e.target.value })} style={{ ...inputStyle, maxWidth: 180, textAlign: 'right' }} />
              </div>
            ))}
          </>
        ) : (
          <>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsExpanded(!isExpanded)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsExpanded(!isExpanded);
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  background: 'var(--sage-l, #E1F5EE)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                🏥
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{clinic.name}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                  {clinic.location} · {clinic.doctor}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, textAlign: 'right' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pink-d)' }}>{clinic.nextAppt}</span>
                <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 8 }}>{isExpanded ? '▲ less' : '▾ more'}</span>
              </div>
            </div>
            {isExpanded ? (
              <div style={{ borderTop: '0.5px solid var(--sand-2)', paddingTop: 10, marginTop: 0 }}>
                {[
                  { key: 'Protocol', val: clinic.protocol, urgent: false },
                  { key: 'Cycle', val: clinic.cycle, urgent: false },
                  { key: 'Next appt', val: clinic.nextAppt, urgent: true },
                  { key: 'Phone', val: clinic.phone, urgent: false },
                ].map((r, i, arr) => (
                  <div
                    key={r.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '7px 0',
                      borderBottom: i < arr.length - 1 ? '0.5px solid #F0EBE5' : 'none',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: 'var(--muted)' }}>{r.key}</span>
                    <span style={{ fontWeight: 600, color: r.urgent ? 'var(--pink)' : 'var(--text)' }}>{r.val}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </SectionCard>

      <SectionCard
        sectionTitle="Ovarian Reserve Baseline Panel"
        sectionDescriptor="Your most important fertility numbers at a glance."
        editing={editingStats}
        onEditToggle={() => setEditingStats(!editingStats)}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
          {stats.map((s, idx) => {
            return (
              <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                <div className="card-label" style={{ marginBottom: 5 }}>
                  <span>{s.label}</span>
                </div>
                {editingStats ? (
                  <>
                    <input value={s.val} onChange={(e) => updateStat(idx, 'val', e.target.value)} style={{ ...inputStyle, marginBottom: 4 }} placeholder="Value" />
                    <input value={s.range || ''} onChange={(e) => updateStat(idx, 'range', e.target.value)} style={inputStyle} placeholder="Range" />
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline' }}>
                      <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, color: 'var(--text)' }}>{s.val}</span>
                      {s.unit && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)', marginLeft: 2 }}>{s.unit}</span>}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{s.range}</div>
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        marginTop: 5,
                        ...(s.badgeGreen ? { background: 'var(--sage-l)', color: 'var(--sage-d)', border: '1px solid var(--sage-m)' } : { background: '#fdf3e0', color: '#92550a', border: '1px solid #f5c97a' }),
                      }}
                    >
                      {s.badge}
                    </span>
                  </>
                )}
                {!editingStats ? (
                  (() => {
                    const a = getKeyNumberAnalysis(s);
                    return <CollapsibleInlineNovaAnalysis sentence={a.sentence} band={a.band} question={a.question} showBand />;
                  })()
                ) : null}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard
        sectionTitle="Recent Labs"
        sectionDescriptor={'Your baseline fertility workup — drawn once\nat the start of care.'}
        descriptorMultiline
        editing={editingLabs}
        onEditToggle={() => setEditingLabs(!editingLabs)}
        onAddResult={addBloodDraw}
        onExport={() =>
          exportSection('Recent_labs', labs.map((l) => `${l.name}: ${l.val} (${l.date})`).join('\n'))
        }
      >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            {sortedDrawsForTabs.map((d) => {
              const active = activeDrawId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setActiveDrawId(d.id)}
                  style={{
                    fontSize: 11,
                    padding: '4px 12px',
                    borderRadius: 20,
                    border: active ? 'none' : '1px solid #E8E0D8',
                    background: active ? '#1A1130' : 'var(--card)',
                    color: active ? '#ffffff' : '#7A6B63',
                    cursor: 'pointer',
                    fontFamily: FONT_BODY,
                    fontWeight: 500,
                  }}
                >
                  {formatDrawTabLabel(d)}
                </button>
              );
            })}
            <button
              type="button"
              onClick={addBloodDraw}
              style={{
                marginLeft: 'auto',
                fontSize: 11,
                padding: '4px 12px',
                borderRadius: 20,
                border: '1px solid #F4C0D1',
                background: '#FBEAF0',
                color: '#993556',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                fontWeight: 600,
              }}
            >
              + Add draw
            </button>
          </div>

          <div
            style={{
              background: 'var(--sand, #f9f6f1)',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 12,
              fontSize: 12,
              color: 'var(--muted)',
              lineHeight: 1.55,
            }}
          >
            These 8 markers are drawn once at the start of care — usually on cycle day 3. Together they give your clinic a full picture of your ovarian reserve, hormonal baseline, and thyroid and prolactin health. Nova explains each result below.
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>Add or edit lab results</span>
            {editingLabs && <button type="button" onClick={addLab} style={{ ...btnSecondary, fontSize: 11 }}>+ Add lab</button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
            {labs.map((l, idx) => {
              return (
                <div key={l.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                  {editingLabs ? (
                    <>
                      <input value={l.name} onChange={(e) => updateLab(idx, 'name', e.target.value)} style={{ ...inputStyle, marginBottom: 4 }} placeholder="Name" />
                      <input value={l.val} onChange={(e) => updateLab(idx, 'val', e.target.value)} style={{ ...inputStyle, marginBottom: 4 }} placeholder="Value" />
                      <input value={l.date} onChange={(e) => updateLab(idx, 'date', e.target.value)} style={{ ...inputStyle, marginBottom: 6 }} placeholder="Date" />
                      <button type="button" onClick={() => removeLab(idx)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: 10, width: '100%' }}>Remove</button>
                    </>
                  ) : (
                    <>
                      <div className="card-label" style={{ marginBottom: 5 }}>
                        <span>{l.name}</span>
                      </div>
                      {(() => {
                        const { num, unit, plain } = splitLabDisplayVal(l.val);
                        const color = l.borderline ? '#92550a' : 'var(--sage)';
                        return (
                          <div style={{ display: 'flex', alignItems: 'baseline' }}>
                            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, color }}>{num}</span>
                            {!plain && unit ? (
                              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)', marginLeft: 2 }}>{unit}</span>
                            ) : null}
                          </div>
                        );
                      })()}
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{l.range}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 1 }}>{l.date}</div>
                      {(() => {
                        const la = getLabRowAnalysis(l);
                        return (
                          <CollapsibleInlineNovaAnalysis
                            sentence={la.sentence}
                            question={la.question}
                            band={la.band}
                            compact
                            showBand={Boolean(la.band)}
                          />
                        );
                      })()}
                    </>
                  )}
                </div>
              );
            })}
          </div>
      </SectionCard>

      <SectionCard
        sectionTitle="Cycle history"
        sectionDescriptor="Your IVF cycle progress and outcomes."
        editing={editingCycle}
        onEditToggle={() => setEditingCycle(!editingCycle)}
        onExport={() => exportSection('Cycle_History', cycleRows.map((r) => `${r.key}: ${r.val}`).join('\n'))}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 8 }}>
          {editingCycle && <button type="button" onClick={addCycleRow} style={{ ...btnSecondary, fontSize: 11 }}>+ Add row</button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          {cycleRows.map((r, i) => (
            <div key={r.key + i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < cycleRows.length - 2 ? '1px solid var(--sand-2)' : 'none', gap: 8 }}>
              {editingCycle ? (
                <>
                  <input value={r.key} onChange={(e) => updateCycleRow(i, 'key', e.target.value)} style={{ ...inputStyle, maxWidth: 120 }} />
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input value={r.val} onChange={(e) => updateCycleRow(i, 'val', e.target.value)} style={{ ...inputStyle, maxWidth: 140 }} />
                    <button type="button" onClick={() => removeCycleRow(i)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: 10 }}>×</button>
                  </div>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>{r.key}</span>
                  {r.pill ? (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 9px',
                        borderRadius: 999,
                        ...(r.pill === 'pink' ? { background: 'var(--pink-l)', color: 'var(--pink-dk)', border: '1px solid var(--pink-m)' } : { background: 'var(--sage-l)', color: 'var(--sage-d)', border: '1px solid var(--sage-m)' }),
                      }}
                    >
                      {r.val}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{r.val}</span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </SectionCard>
      </div>
    </div>
  );
}
